import React, { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { Smartphone, Download, Share2, X } from "lucide-react";

/**
 * Desktop-only QR code that lets restaurant partners & first testers install NelDaari
 * by scanning with their phone. Deep-links to `?source=pwa` so the InstallPrompt banner
 * appears immediately after landing.
 *
 * Mobile users don't need this — they get the standard InstallPrompt banner directly.
 */
export default function InstallQR({ compact = false }) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.matchMedia("(max-width: 640px)").matches);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Always link to the current origin with a PWA flag so we can measure QR-driven installs
  const installUrl = `${window.location.origin}/?source=pwa-qr`;

  // On mobile: render a small "Share app" button that uses the Web Share API instead of a QR
  const nativeShare = async () => {
    try {
      await navigator.share({
        title: "NelDaari",
        text: t("qr.shareText"),
        url: installUrl,
      });
    } catch { /* ignore user cancel */ }
  };

  if (isMobile) {
    if (!navigator.share) return null;
    return (
      <button
        data-testid="pwa-mobile-share"
        onClick={nativeShare}
        className="inline-flex items-center gap-2 rounded-full bg-white border border-sand text-ink text-sm font-medium px-4 py-2 hover:border-brand-red hover:text-brand-red transition-colors"
      >
        <Share2 className="w-4 h-4" />
        {t("qr.shareBtn")}
      </button>
    );
  }

  if (compact) {
    return (
      <>
        <button
          data-testid="pwa-qr-compact-open"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-white border border-sand text-ink text-sm font-medium px-4 py-2 hover:border-brand-red hover:text-brand-red transition-colors"
        >
          <Smartphone className="w-4 h-4" />
          {t("qr.compactBtn")}
        </button>
        {modalOpen && (
          <QRModal url={installUrl} onClose={() => setModalOpen(false)} t={t} />
        )}
      </>
    );
  }

  return (
    <div
      data-testid="pwa-qr-section"
      className="bg-gradient-to-br from-[#FFF5F6] to-white border border-sand rounded-3xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6"
    >
      <div className="shrink-0 bg-white rounded-2xl p-3 shadow-[0_8px_30px_-8px_rgba(227,38,54,0.25)]" data-testid="pwa-qr-canvas-wrap">
        <QRCodeSVG
          value={installUrl}
          size={148}
          bgColor="#FFFFFF"
          fgColor="#1A1A1A"
          level="M"
          includeMargin={false}
          imageSettings={{
            src: "/icon-192.png",
            height: 32,
            width: 32,
            excavate: true,
          }}
        />
      </div>
      <div className="flex-1 min-w-0 text-center sm:text-left">
        <div className="text-xs uppercase tracking-[0.25em] text-brand-red mb-1">{t("qr.badge")}</div>
        <h3 className="font-display text-xl sm:text-2xl font-semibold text-ink">{t("qr.title")}</h3>
        <p className="text-sm text-mute mt-2 leading-relaxed">{t("qr.description")}</p>
        <div className="mt-4 flex flex-wrap gap-2 justify-center sm:justify-start">
          <span className="inline-flex items-center gap-1.5 text-xs rounded-full bg-white border border-sand px-3 py-1.5">
            <Download className="w-3 h-3 text-brand-red" /> {t("qr.tagOffline")}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs rounded-full bg-white border border-sand px-3 py-1.5">
            <Smartphone className="w-3 h-3 text-brand-red" /> {t("qr.tagInstallable")}
          </span>
        </div>
      </div>
    </div>
  );
}

function QRModal({ url, onClose, t }) {
  return (
    <div
      data-testid="pwa-qr-modal"
      className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-[9998] grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-6 max-w-sm w-full relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="pwa-qr-modal-close"
          onClick={onClose}
          className="absolute top-3 right-3 text-mute hover:text-ink"
          aria-label={t("common.close")}
        >
          <X className="w-4 h-4" />
        </button>
        <h3 className="font-display text-xl font-semibold text-ink text-center">{t("qr.title")}</h3>
        <p className="text-sm text-mute mt-2 text-center leading-relaxed">{t("qr.description")}</p>
        <div className="mt-4 flex justify-center">
          <div className="bg-white border border-sand rounded-2xl p-3">
            <QRCodeSVG value={url} size={200} level="M" imageSettings={{ src: "/icon-192.png", height: 44, width: 44, excavate: true }} />
          </div>
        </div>
      </div>
    </div>
  );
}
